import type { CockpitTimelineDay } from '@/lib/api'
import { domainColor, fmtTime } from '../lib'

const DAY = 1440 // minutes

/** Today's plan lane (PLACEHOLDER windows, tinted) over the actual lane
 * (timed blocks, solid; future lighter; in-progress outlined) with a now
 * cursor. Port of the plugin CockpitView's _dayStrip SVG. */
export function DayStrip({
  day,
  showNow,
  onOpen,
}: {
  day: CockpitTimelineDay
  showNow: boolean
  onOpen: (path: string, name: string, meta: string) => void
}) {
  const dayStart = new Date(day.day_start).getTime()
  const toMin = (ms: number) => Math.max(0, Math.min(DAY, (ms - dayStart) / 60000))
  const nowMin = toMin(Date.now())

  const hours: number[] = []
  for (let hh = 3; hh < 24; hh += 3) hours.push(hh)

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${DAY} 56`} preserveAspectRatio="none" className="w-full h-14 block">
        {hours.map((hh) => (
          <line
            key={hh}
            x1={hh * 60}
            x2={hh * 60}
            y1={0}
            y2={56}
            stroke="rgba(235,235,235,0.08)"
            strokeWidth={1}
          />
        ))}
        {day.plan.map((p, i) => {
          const x = toMin(p.start_ms)
          const w = Math.max(2, toMin(p.end_ms) - x)
          return (
            <rect
              key={`p${i}`}
              x={x}
              y={2}
              width={w}
              height={15}
              rx={2}
              fill={domainColor(p.domain)}
              fillOpacity={0.32}
              stroke={domainColor(p.domain)}
              strokeOpacity={0.6}
              strokeWidth={0.6}
            >
              <title>{`plan · ${p.summary} · ${fmtTime(p.start)}–${fmtTime(p.end)}`}</title>
            </rect>
          )
        })}
        {day.actual.map((a, i) => {
          const x = toMin(a.start_ms)
          const w = Math.max(2, toMin(a.end_ms) - x)
          const done = a.status === 'completed' || a.status === 'in_progress'
          return (
            <g key={`a${i}`}>
              <rect
                x={x}
                y={20}
                width={w}
                height={26}
                rx={2}
                fill={domainColor(a.domain)}
                fillOpacity={done ? 0.95 : 0.4}
                className="cursor-pointer"
                onClick={() => onOpen(a.path, a.name, `${a.domain}${a.workstream ? ` / ${a.workstream}` : ''}`)}
              >
                <title>{`${a.name} · ${a.workstream || ''} · ${fmtTime(a.start)}–${fmtTime(a.end)} · ${a.status || ''}`}</title>
              </rect>
              {a.status === 'in_progress' && (
                <rect x={x} y={20} width={w} height={26} rx={2} fill="none" stroke="#facc15" strokeWidth={1.5} />
              )}
            </g>
          )
        })}
        {showNow && nowMin > 0 && nowMin < DAY && (
          <line x1={nowMin} x2={nowMin} y1={0} y2={56} stroke="#f87171" strokeWidth={1.5} />
        )}
      </svg>
      {/* HTML hour axis (SVG text would distort under preserveAspectRatio:none) */}
      <div className="relative h-4 text-[9px] text-muted-foreground">
        {[0, 3, 6, 9, 12, 15, 18, 21].map((hh) => (
          <span key={hh} className="absolute -translate-x-1/2" style={{ left: `${(hh / 24) * 100}%` }}>
            {hh === 0 ? '12a' : hh === 12 ? '12p' : (hh % 12) + (hh < 12 ? 'a' : 'p')}
          </span>
        ))}
      </div>
    </div>
  )
}
